import { useState } from "react";
import { Copy, Check, LinkSimple, Share } from "@phosphor-icons/react";

/**
 * A pill-style box that shows a shareable URL and a one-click "Copy" button.
 *
 * Props:
 *  - link: the full URL to share
 *  - label: (optional) small-caps label above the box (default: "Public share link")
 *  - description: (optional) helper text below
 *  - compact: render in a smaller single-row layout
 *  - testidPrefix: base string for data-testid attrs (default: "share-link")
 */
export default function ShareLinkBox({
  link,
  label = "Public share link",
  description,
  compact = false,
  testidPrefix = "share-link",
}) {
  const [copied, setCopied] = useState(false);

  if (!link) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback: select + document.execCommand (for old browsers)
      const ta = document.createElement("textarea");
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1800); }
      catch { /* ignore */ }
      ta.remove();
    }
  };

  const openMail = () => {
    const mailto = `mailto:?subject=${encodeURIComponent("Review our quote")}&body=${encodeURIComponent("You can view the quote here: " + link)}`;
    window.location.href = mailto;
  };

  if (compact) {
    return (
      <div data-testid={`${testidPrefix}-compact`} className="flex items-center gap-2 w-full">
        <div className="flex-1 flex items-center gap-2 min-w-0 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1.5">
          <LinkSimple size={12} className="text-slate-400 flex-shrink-0" />
          <input
            data-testid={`${testidPrefix}-input`}
            value={link}
            readOnly
            onFocus={(e) => e.target.select()}
            className="flex-1 bg-transparent text-[11px] font-mono text-slate-600 truncate focus:outline-none"
          />
        </div>
        <button
          data-testid={`${testidPrefix}-copy`}
          onClick={handleCopy}
          className={`inline-flex items-center gap-1 h-7 px-2 text-[11px] font-bold rounded-sm transition-colors ${
            copied ? "bg-emerald-600 text-white" : "bg-blue-700 text-white hover:bg-blue-800"
          }`}
          title={copied ? "Copied!" : "Copy to clipboard"}
        >
          {copied ? <><Check size={11} weight="bold" /> Copied</> : <><Copy size={11} weight="bold" /> Copy</>}
        </button>
      </div>
    );
  }

  return (
    <div data-testid={testidPrefix} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-sm p-4">
      <div className="flex items-center gap-1.5 mb-2">
        <Share size={12} weight="bold" className="text-blue-700" />
        <p className="text-[10px] font-black uppercase tracking-wider text-blue-800">{label}</p>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 flex items-center gap-2 min-w-0 bg-white border border-blue-200 rounded-sm px-3 py-2">
          <LinkSimple size={13} className="text-slate-400 flex-shrink-0" />
          <input
            data-testid={`${testidPrefix}-input`}
            value={link}
            readOnly
            onFocus={(e) => e.target.select()}
            className="flex-1 bg-transparent text-xs font-mono text-slate-700 truncate focus:outline-none"
          />
        </div>
        <button
          data-testid={`${testidPrefix}-copy`}
          onClick={handleCopy}
          className={`inline-flex items-center gap-1 h-9 px-3 text-xs font-bold rounded-sm transition-colors whitespace-nowrap ${
            copied ? "bg-emerald-600 text-white" : "bg-blue-700 text-white hover:bg-blue-800"
          }`}
        >
          {copied ? <><Check size={13} weight="bold" /> Copied</> : <><Copy size={13} weight="bold" /> Copy</>}
        </button>
        <button
          data-testid={`${testidPrefix}-email`}
          onClick={openMail}
          className="h-9 px-3 text-xs font-bold rounded-sm border border-slate-300 text-slate-700 hover:bg-white transition-colors whitespace-nowrap"
          title="Open your email client to forward this link"
        >
          Email
        </button>
      </div>
      <p className="text-[11px] text-blue-900/70 leading-snug">
        {description || "Anyone with this link can view the quote without signing in. Forward it to whoever needs to approve the purchase."}
      </p>
    </div>
  );
}
